import axios from '../../axios/index.js';

export default {
    /**
     * Retrieves Shop Drawing.
     *
     * @param shopDrawingId Shop Drawing ID
     * @param shopDrawingRevisionId Shop Drawing revision ID
     *
     * @returns {*}
     */
    getShopDrawing: function (shopDrawingId, shopDrawingRevisionId) {
        return axios.get(
            '/api/v1/shop-drawings/' + shopDrawingId + (shopDrawingRevisionId !== null ? '/' + shopDrawingRevisionId : ''));
    },

    /**
     * Retrieves project information for Shop Drawings sidebar.
     *
     * @param shopDrawingId Shop Drawing ID
     * @param shopDrawingRevisionId Shop Drawing revision ID
     *
     * @returns {*}
     */
    getProjectInfo: function (shopDrawingId, shopDrawingRevisionId) {
        return axios.get(
            '/api/v1/shop-drawings/project-info/' + shopDrawingId + (shopDrawingRevisionId !== null ? '/' + shopDrawingRevisionId : ''));
    },

    /**
     * Set draft approval.
     *
     * @param shopDrawingRevisionDraftId Shop Drawing revision draft ID
     * @param approval Approval state
     * @param note Approval comment
     * @param rejectionId
     * @param attachments Approval attached files
     *
     * @returns {*}
     */
    setDraftApproval: function (shopDrawingRevisionDraftId, approval, note, rejectionId, attachments) {
        let formData = new FormData();

        formData.append('note', note);
        formData.append('rejectionId', rejectionId);
        formData.append('approval', approval);

        for (let i = 0; i < attachments.length; i++) {
            formData.append('attachments[' + i + ']', attachments[i].file);
        }

        return axios.post('/api/v1/shop-drawings/set-draft-approval/' + shopDrawingRevisionDraftId, formData);
    },

    /**
     * Set draft approval.
     *
     * @param {integer} shopDrawingRevisionDraftId Shop Drawing revision draft ID
     * @param {boolean} submitted submitted state
     * @param {string} note submitted note
     * @param {boolean|undefined} quoteAdjustmentNeeded
     *
     * @returns {*}
     */
    setDraftSubmitted: function (shopDrawingRevisionDraftId, submitted, note, quoteAdjustmentNeeded) {
        const data = {
            submitted: submitted,
            note: note,
            quoteAdjustmentNeeded: false,
        };
        if (quoteAdjustmentNeeded !== undefined) {
            data.quoteAdjustmentNeeded = quoteAdjustmentNeeded;
        }
        return axios.post('/api/v1/shop-drawings/set-draft-submitted/' + shopDrawingRevisionDraftId, data);
    },

    /**
     * Remove draft.
     *
     * @param shopDrawingRevisionDraftId Shop Drawing revision draft ID
     *
     * @returns {*}
     */
    removeDraft: function (shopDrawingRevisionDraftId) {
        return axios.post(
            '/api/v1/shop-drawings/remove-draft/' + shopDrawingRevisionDraftId);
    },

    /**
     * Upload new draft.
     *
     * @param shopDrawingRevisionDraftId Shop Drawing revision draft ID
     * @param files binary file
     * @param attachments -- draft additional files
     * @param installationDetailsSets -- number of installation details sets
     * @returns {*}
     */
    storeUploadedDraft: function (shopDrawingRevisionDraftId, files, attachments, installationDetailsSets, installationDetailsRevised, newPositions, revisedPositions, metalProfilesDetailsAttachments, note, quoteAdjustmentsNeeded) {
        let formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append('files[]', files[i]);
        }
        formData.append('installationDetailsSets', installationDetailsSets);
        formData.append('installationDetailsRevised', installationDetailsRevised);
        formData.append('newPositions', newPositions);
        formData.append('revisedPositions', revisedPositions);
        formData.append('note', note);
        formData.append('quoteAdjustmentNeeded', quoteAdjustmentsNeeded);
        for (let i = 0; i < attachments.length; i++) {
            formData.append('attachments[]', attachments[i]);
        }
        for (let i = 0; i < metalProfilesDetailsAttachments.length; i++) {
            formData.append('metalProfilesDetailsAttachment[]', metalProfilesDetailsAttachments[i]);
        }

        return axios.post(
            '/api/v1/shop-drawings/upload-draft/' + shopDrawingRevisionDraftId,
            formData);
    },

    /**
     * Get Shop Drawings file upload URL.
     *
     * @param type File type
     * @param shopDrawingRevisionId Shop Drawing revision ID
     * @param fileName File name
     *
     * @returns {*}
     */
    getUploadUrl: function (type, shopDrawingRevisionId, fileName) {
        return axios.get('/api/v1/shop-drawings/file-upload-url/' + type + '/' + shopDrawingRevisionId, {
            params: {
                file_name: fileName,
            },
        });
    },

    /**
     * Retrieves drafters list.
     *
     * @returns {*}
     */
    getDrafters: function () {
        return axios.get('/api/v1/shop-drawings/drafters');
    },

    /**
     * Retrieves rejections list
     * @returns {*}
     */
    getRejections: function () {
        return axios.get('/api/v1/shop-drawings/rejections');
    },

    /**
     * Saves signed Shop Drawing to API.
     *
     * @param shopDrawingRevisionId Shop Drawing revision ID
     * @param note Signed Shop Drawing note
     * @param file Signed Shop Drawing file
     *
     * @returns {*}
     */
    saveSignedShop: function (shopDrawingRevisionId, note, file) {
        let formData = new FormData();

        formData.append('file', file);
        formData.append('note', note);

        return axios.post('/api/v1/shop-drawings/save-signed-shop/' + shopDrawingRevisionId, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    /**
     * Cancels Shop Drawing request.
     *
     * @param shopDrawingRevisionId Shop Drawing revision ID
     * @param note Signed Shop Drawing note
     *
     * @returns {*}
     */
    cancelShopDrawingRequest: function (shopDrawingRevisionId, note) {
        let formData = new FormData();

        formData.append('note', note);

        return axios.post('/api/v1/shop-drawings/cancel/' + shopDrawingRevisionId, formData);
    },

    /**
     * Holds Shop Drawing request.
     *
     * @param shopDrawingRevisionId Shop Drawing revision ID
     * @param reason hold reason
     * @param due_date hold due date
     *
     * @returns {*}
     */
    holdShopDrawingRequest: function (shopDrawingRevisionId, reason, due_date, status) {
        let formData = new FormData();
        formData.append('reason', reason);
        formData.append('due_date', due_date);
        formData.append('status', status);

        return axios.post('/api/v1/shop-drawings/hold/' + shopDrawingRevisionId, formData);
    },

    /**
     * Unholds Shop Drawing request.
     *
     * @param shopDrawingRevisionId Shop Drawing revision ID
     *
     * @returns {*}
     */
    unholdShopDrawingRequest: function (shopDrawingRevisionId) {
        return axios.post('/api/v1/shop-drawings/unhold/' + shopDrawingRevisionId);
    },

    /**
     * Chnage hold due date Shop Drawing request.
     *
     * @param shopDrawingRevisionId Shop Drawing revision ID
     * @param due_date new hold due date
     * @returns {*}
     */
    holdShopDrawingDueDateChangeRequest: function (shopDrawingRevisionId, due_date) {
        let formData = new FormData();
        formData.append('due_date', due_date);
        return axios.post('/api/v1/shop-drawings/hold-due-date/' + shopDrawingRevisionId, formData);
    },

    /**
     * Requests for shops.
     *
     * @param shopDrawingRevisionId Shop Drawing revision ID
     *
     * @returns {*}
     */
    requestForShops: function (shopDrawingRevisionId) {
        return axios.post('/api/v1/shop-drawings/request-shops/' + shopDrawingRevisionId);
    },

    /**
     * Requests for revision.
     *
     * @param shopDrawingRevisionId Shop Drawing revision ID
     *
     * @returns {*}
     */
    requestForRevision: function (shopDrawingRevisionId) {
        return axios.post('/api/v1/shop-drawings/request-revision/' + shopDrawingRevisionId);
    },

    /**
     * Gets project quotes.
     *
     * @param projectId Project ID
     * @param proposalId Proposal ID
     *
     * @returns {*}
     */
    getQuotes: function (projectId, proposalId) {
        return axios.get('/api/v1/shop-drawings/quotes/' + projectId + (proposalId ? '/' + proposalId : ''));
    },

    /**
     * Gets architectural drawings.
     *
     * @param projectId Project ID
     *
     * @returns {*}
     */
    getArchitecturalDrawings: function (projectId) {
        return axios.get('/api/v1/shop-drawings/architectural-drawings/' + projectId);
    },


    /**
     * Gets revision request data.
     *
     * @param {number} proposalId
     *
     * @returns {*}
     */
    getRevisionRequestDetails: function (proposalId) {
        return axios.get('/api/v1/shop-drawings/revision-request/' + proposalId);
    },

    /**
     * Gets revision proposal 3D CAD link.
     *
     * @param {number} revisionId
     *
     * @param generateNew
     * @returns {*}
     */
    getCad3DDocumentLink: function (revisionId, generateNew = null) {
        let url = '/api/v1/shop-drawings/get-3d-cad-document/'+ revisionId;
        if (generateNew) {
            url += '?generateNew=1';
        }
        return axios.get(url)
    },

    /**
     * Updates task information.
     *
     * @param shopDrawingRevisionId Shop Drawing revision ID
     * @param data Task information changes
     *
     * @returns {*}
     */
    updateTaskInformation: function (shopDrawingRevisionId, data) {
        let formData = new FormData();

        if (Object.keys(data).length !== 0 && data.constructor === Object) {
            Object.keys(data).forEach(function (key) {
                if (typeof data[key].id !== 'undefined') {
                    formData.append(key + '_id', data[key].id);
                } else {
                    if (Array.isArray(data[key]) && data[key].length) {
                        for (let i = 0; i < data[key].length; i++) {
                            if (key === 'architectural_drawings') {
                                formData.append(key + '[' + i + ']', data[key][i].link);
                            } else {
                                formData.append(key + '[' + i + ']',
                                    (typeof data[key][i].file !== 'undefined') ? data[key][i].file : data[key][i]);
                            }
                        }
                    } else {
                        if (Array.isArray(data[key])) {
                            // A workaround for sending empty arrays
                            formData.append(key + '[]', null);
                        } else {
                            formData.append(key, data[key]);
                        }
                    }
                }
            });
        }

        return axios.post('/api/v1/shop-drawings/task-information/' + shopDrawingRevisionId, formData);
    },

    /**
     * Gets shop drawings list.
     *
     * @param httpOptions http options from the vuetable-2
     *
     * @returns {*}
     */
    getShopDrawingsList: function (httpOptions) {
        return axios.get('/api/v1/shop-drawings/get-list', httpOptions);
    },

    /**
     * Gets shop action required list.
     *
     * @param httpOptions http options from the vuetable-2
     *
     * @returns {*}
     */
    getActionRequiredList: function (httpOptions) {
        return axios.get('/api/v1/shop-drawings/get-action-required-list', httpOptions);
    },

    /**
     * Gets Shop Drawing revision statuses.
     *
     * @returns {*}
     */
    getShopDrawingRevisionStatuses: function () {
        return axios.get('/api/v1/shop-drawings/get-revision-statuses');
    },

    /**
     * Gets Shop Drawing revision hold statuses.
     *
     * @returns {*}
     */
    getShopDrawingRevisionHoldStatuses: function () {
        return axios.get('/api/v1/shop-drawings/get-revision-hold-statuses');
    },

    /**
     * Gets Shop Drawing action required unassigned count.
     *
     * @returns {*}
     */
    getActionRequiredListUnassignedCount: function () {
        return axios.get('/api/v1/shop-drawings/get-action-required-unassigned-count');
    },
    getActionRequiredListAssignedCount: function () {
        return axios.get('/api/v1/shop-drawings/get-action-required-assigned-count');
    },
    getActionRequiredListWorkloadCount: function () {
        return axios.get('/api/v1/shop-drawings/get-action-required-workload-count');
    },

    /**
     * Requests for revision.
     *
     * @param shopDrawingRevisionId Shop Drawing revision ID
     *
     * @returns {*}
     */
    assignsDrafter: function (shopDrawingRevisionId) {
        return axios.post('/api/v1/shop-drawings/assign-drafter/' + shopDrawingRevisionId);
    },

    /**
     * Submit to Drafter
     *
     * @param shopDrawingRevisionId Shop Drawing revision ID
     *
     * @returns {*}
     */
    submitToDrafter: function (shopDrawingRevisionId) {
        return axios.post('/api/v1/shop-drawings/submit-to-drafter/' + shopDrawingRevisionId);
    },

    /**
     * Gets shop drawings XLSX download link.
     *
     * @param options filter options
     *
     * @returns {*}
     */
    getShopDrawingsListToExcel: function (options) {
        return axios.get('/api/v1/shop-drawings/get-list-to-excel-url', { params: options });
    },

    /**
     * Get shop drawing timeline data
     *
     * @returns {*}
     */
    getTimelineData: function (options) {
        return axios.get('/api/v1/shop-drawings/get-timeline-data/' + options.id, { params: { page: options.page } });
    },


    /**
     * Upload new draft.
     *
     * @param additionalRequestId Shop Drawing revision additional request ID
     * @param note drafter note
     * @param file binary file
     * @param attachments -- draft additional files
     * @returns {*}
     */
    submitAdditionalRequest: function (additionalRequestId, note, file, attachments) {
        let formData = new FormData();
        formData.append('file', file);
        formData.append('note', note);

        for (let i = 0; i < attachments.length; i++) {
            formData.append('attachments[]', attachments[i]);
        }

        return axios.post(
            '/api/v1/shop-drawings/submit-additional-request/' + additionalRequestId,
            formData);
    },
    /**
     * Assign drafter to additional request.
     *
     * @param additionalRequestId Shop Drawing revision additional request ID
     * @param drafter_id Drafter id
     * @returns {*}
     */
    assignDrafterToAdditionalRequest: function (additionalRequestId, drafter_id) {
        let formData = new FormData();
        formData.append('drafter_id', drafter_id);

        return axios.post(
            '/api/v1/shop-drawings/assign-drafter-to-additional-request/' + additionalRequestId,
            formData);
    },

    /**
     * Cancels Shop Drawing Additional Request request.
     *
     * @param requestId Shop Drawing Additional Request ID
     * @param note Signed Shop Drawing note
     *
     * @returns {*}
     */
    cancelShopDrawingAdditionalRequest: function (requestId, note) {
        let formData = new FormData();

        formData.append('note', note);

        return axios.post('/api/v1/shop-drawings/cancel-additional-request/' + requestId, formData);
    },

    /**
     * Creates New Shop Drawing request.
     *
     * @param projectId project id
     *
     * @returns {*}
     */
    createShopDrawing: function (projectId) {
        let formData = new FormData();

        formData.append('project_id', projectId);

        return axios.post('/api/v1/shop-drawings/create/', formData);
    },

    /**
     * Retrieves shops filters information.
     *
     * @returns {*}
     */
    getFiltersInformation () {
        return axios.get('/api/v1/shop-drawings/filters');
    },

    /**
     * Links Shop Drawings.
     *
     * @param shopIdA Shop Drawing ID
     * @param shopIdB Shop Drawing ID
     *
     * @returns {*}
     */
    linkShopDrawingRequest: function (shopIdA, shopIdB) {
        let formData = new FormData();

        formData.append('shop_id_a', shopIdA);
        formData.append('shop_id_b', shopIdB);

        return axios.post('/api/v1/shop-drawings/link/', formData);
    },

    createNewInstDetailsForShop: function (shopId) {
        let formData = new FormData();

        formData.append('shop_id', shopId);
        return axios.post('/api/v1/shop-drawings/create-inst-details', formData);
    },

    getCadFile: function (revisionId) {
        return axios.get('/api/v1/shop-drawings/get-cad-file/' + revisionId);
    },

    getInstDetailsRequest: function (shopId) {
        return axios.get('/api/v1/shop-drawings/get-linked-inst-details/' + shopId);
    },

    createQuotComparisonJob: function (shopId) {
        let formData = new FormData();

        formData.append('shop_id', shopId);
        return axios.post('/api/zoho/shop-drawings/quote-comparison/create-job', formData);
    },

    quoteComparisonJobDetails: function (jobId) {
        return axios.get('/api/zoho/shop-drawings/quote-comparison/'+jobId+'/status');
    },

    calculateEstDates: () => {
        return axios.get('/api/v1/shop-drawings/calculate-est-dates/');
    },

    getLinkedOrders: function (projectId, proposalId) {
        return axios.get(`/api/v1/shop-drawings/linked-orders/${projectId}/${proposalId}` )
    },

    addDraftNote: function (draftId, note, attachments) {
        return axios.post('/api/v1/shop-drawings/add-draft-note/' + draftId, {
            note: note,
            attachments: attachments.length ? Object.values(attachments).map(file => file.file) : attachments,
        });
    },

    getPriorityOptions() {
        return axios.get('/api/v1/shop-drawings/priority-options');
    },

    getPriorityItems() {
        return axios.get('/api/v1/shop-drawings/get-priority-items');
    },

    setPriorityItems (options) {
        let formData = new FormData();
        formData.append('data', JSON.stringify(options));
        return axios.post('/api/v1/shop-drawings/set-priorities', formData);
    },

    calculateDifficultyLevel (projectId, proposalId) {
        return axios.get('/api/v1/shop-drawings/calculate-difficulty-level/' + projectId + '/' + proposalId);
    }
};
